import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Copy, Edit3, Link2, Trash2 } from "lucide-react";
import { createUtmCampaign, deleteUtmCampaign, fetchUtmCampaigns, updateUtmCampaign } from "@/lib/utmApi";

const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL;
if (!PUBLIC_SITE_URL) throw new Error("REACT_APP_PUBLIC_SITE_URL is required");

const defaults = { name: "Aliensense NXS Cooperation", source: "aliensense", medium: "partner_referral", campaign: "nxs_cooperation", content: "announcement", term: "" };
const presets = [
  ["Partner Referral", "partner_referral"],
  ["LinkedIn / Social", "social"],
  ["Email", "email"],
  ["Website Referral", "referral"],
  ["QR", "qr"],
];
const normalize = (value) => value.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");

const buildUrl = (form) => {
  const params = new URLSearchParams({ utm_source: normalize(form.source), utm_medium: normalize(form.medium), utm_campaign: normalize(form.campaign) });
  if (normalize(form.content)) params.set("utm_content", normalize(form.content));
  if (normalize(form.term)) params.set("utm_term", normalize(form.term));
  return `${PUBLIC_SITE_URL.replace(/\/$/, "")}/?${params.toString()}`;
};

const fallbackCopy = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
};

export function AdminUtmBuilder({ token, onUnauthorized }) {
  const [form, setForm] = useState(defaults);
  const [campaigns, setCampaigns] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [message, setMessage] = useState("");
  const generatedUrl = useMemo(() => buildUrl(form), [form]);
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const load = useCallback(async () => {
    try {
      setCampaigns(await fetchUtmCampaigns(token));
    } catch (error) {
      if (error.response?.status === 401) onUnauthorized();
      else setMessage("Could not load saved campaign links.");
    }
  }, [onUnauthorized, token]);

  useEffect(() => { load(); }, [load]);

  const copyUrl = async (url, label = "Link") => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(url);
      setMessage(`${label} copied.`);
    } catch {
      try {
        if (fallbackCopy(url)) {
          setMessage(`${label} copied.`);
          return;
        }
      } catch {
        // The generated URL remains visible for manual selection.
      }
      setMessage("Clipboard access is blocked. Select the generated URL and copy it manually.");
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setMessage("Saving campaign link…");
    try {
      const payload = { ...form, source: normalize(form.source), medium: normalize(form.medium), campaign: normalize(form.campaign), content: normalize(form.content), term: normalize(form.term) };
      if (editingId) await updateUtmCampaign(token, editingId, payload);
      else await createUtmCampaign(token, payload);
      setMessage(editingId ? "Campaign link updated." : "Campaign link saved.");
      setEditingId(null);
      setForm(defaults);
      await load();
    } catch (error) {
      if (error.response?.status === 401) onUnauthorized();
      else setMessage("Could not save this campaign link.");
    }
  };

  const edit = (campaign) => {
    setEditingId(campaign.campaign_id);
    setForm({ name: campaign.name, source: campaign.source, medium: campaign.medium, campaign: campaign.campaign, content: campaign.content, term: campaign.term });
    setMessage(`Editing ${campaign.name}.`);
  };

  const confirmDelete = async () => {
    try {
      await deleteUtmCampaign(token, deleteCandidate.campaign_id);
      setDeleteCandidate(null);
      setMessage("Campaign link deleted.");
      await load();
    } catch (error) {
      if (error.response?.status === 401) onUnauthorized();
      else setMessage("Could not delete this campaign link.");
    }
  };

  return (
    <section className="admin-utm" data-testid="admin-utm-builder">
      <div className="admin-utm-heading"><p className="overline" data-testid="admin-utm-overline">Campaign Attribution</p><h2 data-testid="admin-utm-title">UTM Link Builder</h2><p data-testid="admin-utm-description">Create trackable partner, social, email, website, and QR links without changing the public page.</p></div>
      <div className="admin-utm-layout">
        <form className="admin-utm-form" onSubmit={save} data-testid="admin-utm-form">
          <p className="admin-utm-base" data-testid="admin-utm-base-url"><span>Destination</span><strong>{PUBLIC_SITE_URL}</strong></p>
          <label data-testid="admin-utm-name-label"><span>Internal link name</span><input required minLength={2} maxLength={100} value={form.name} onChange={(event) => update("name", event.target.value)} data-testid="admin-utm-name-input" /></label>
          <div className="admin-utm-field-grid">
            <label data-testid="admin-utm-source-label"><span>Source</span><input required value={form.source} onChange={(event) => update("source", event.target.value)} data-testid="admin-utm-source-input" /></label>
            <label data-testid="admin-utm-medium-label"><span>Medium</span><input required value={form.medium} onChange={(event) => update("medium", event.target.value)} data-testid="admin-utm-medium-input" /></label>
            <label data-testid="admin-utm-campaign-label"><span>Campaign</span><input required value={form.campaign} onChange={(event) => update("campaign", event.target.value)} data-testid="admin-utm-campaign-input" /></label>
            <label data-testid="admin-utm-content-label"><span>Content (optional)</span><input value={form.content} onChange={(event) => update("content", event.target.value)} data-testid="admin-utm-content-input" /></label>
            <label data-testid="admin-utm-term-label"><span>Term (optional)</span><input value={form.term} onChange={(event) => update("term", event.target.value)} data-testid="admin-utm-term-input" /></label>
          </div>
          <div className="admin-utm-presets" data-testid="admin-utm-presets"><span>Quick medium</span><div>{presets.map(([label, value]) => <button type="button" className={form.medium === value ? "active" : ""} onClick={() => update("medium", value)} key={value} data-testid={`admin-utm-preset-${value}-button`}>{label}</button>)}</div></div>
          <div className="admin-utm-preview" data-testid="admin-utm-preview"><span>Generated URL</span><code data-testid="admin-utm-generated-url">{generatedUrl}</code><button type="button" onClick={() => copyUrl(generatedUrl, "Generated link")} data-testid="admin-utm-copy-generated-button"><Copy size={16} />Copy URL</button></div>
          <div className="admin-utm-form-actions"><button type="submit" data-testid="admin-utm-save-button"><Check size={16} />{editingId ? "Update saved link" : "Save campaign link"}</button><button type="button" onClick={() => { setEditingId(null); setForm(defaults); setMessage(""); }} data-testid="admin-utm-reset-button">Reset</button></div>
        </form>
        <div className="admin-utm-guide" data-testid="admin-utm-guide"><Link2 size={22} aria-hidden="true" /><h3 data-testid="admin-utm-guide-title">Naming guide</h3><dl><div><dt>Source</dt><dd>Who publishes it: aliensense, linkedin, distributor_name</dd></div><div><dt>Medium</dt><dd>How it is shared: partner_referral, social, email, referral, qr</dd></div><div><dt>Campaign</dt><dd>The initiative: nxs_cooperation, mobility_launch</dd></div><div><dt>Content</dt><dd>The specific placement: linkedin_post, newsletter, homepage_banner</dd></div></dl></div>
      </div>
      {message && <p className="admin-status" role="status" data-testid="admin-utm-message">{message}</p>}
      <div className="admin-utm-history" data-testid="admin-utm-history"><div><p className="overline">Saved history</p><h3 data-testid="admin-utm-history-title">Reusable campaign links</h3></div>{campaigns.length === 0 ? <p className="admin-utm-empty" data-testid="admin-utm-empty-message">No campaign links saved yet.</p> : <div className="admin-utm-history-list">{campaigns.map((campaign) => <article key={campaign.campaign_id} data-testid={`admin-utm-history-${campaign.campaign_id}`}><div><strong data-testid={`admin-utm-history-name-${campaign.campaign_id}`}>{campaign.name}</strong><span data-testid={`admin-utm-history-meta-${campaign.campaign_id}`}>{campaign.source} · {campaign.medium} · {campaign.campaign}</span><code data-testid={`admin-utm-history-url-${campaign.campaign_id}`}>{campaign.generated_url}</code></div><div><button type="button" onClick={() => copyUrl(campaign.generated_url, campaign.name)} data-testid={`admin-utm-history-copy-${campaign.campaign_id}-button`}><Copy size={15} />Copy</button><button type="button" onClick={() => edit(campaign)} data-testid={`admin-utm-history-edit-${campaign.campaign_id}-button`}><Edit3 size={15} />Edit</button><button type="button" className="danger" onClick={() => setDeleteCandidate(campaign)} data-testid={`admin-utm-history-delete-${campaign.campaign_id}-button`}><Trash2 size={15} />Delete</button></div></article>)}</div>}</div>
      {deleteCandidate && <div className="admin-confirm-backdrop" role="presentation" data-testid="admin-utm-delete-backdrop"><div className="admin-confirm-dialog" role="dialog" aria-modal="true" data-testid="admin-utm-delete-dialog"><h3 data-testid="admin-utm-delete-title">Delete this campaign link?</h3><p data-testid="admin-utm-delete-description">The URL will still work, but it will be removed from saved history.</p><div><button type="button" onClick={() => setDeleteCandidate(null)} data-testid="admin-utm-delete-cancel-button">Cancel</button><button type="button" className="danger" onClick={confirmDelete} data-testid="admin-utm-delete-confirm-button">Delete</button></div></div></div>}
    </section>
  );
}